import { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { MessageCircle, Save, CheckCircle, AlertCircle, Phone, Link2, Key, Server, Info, Send, Loader2, Plus, Trash2, ToggleLeft, BarChart3, Shield } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { clearAppCache } from '@/utils/cacheUtils';

export default function WhatsAppSettings({ user, onLogout }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [formData, setFormData] = useState({
    instance_id: '',
    access_token: '',
    cta_url: '',
    cta_phone_number: ''
  });

  // Multiple numbers
  const [numbers, setNumbers] = useState([]);
  const [showAddNumber, setShowAddNumber] = useState(false);
  const [newNumber, setNewNumber] = useState({ label: '', instance_id: '', access_token: '' });
  const [addingNumber, setAddingNumber] = useState(false);
  const [waStats, setWaStats] = useState(null);

  useEffect(() => {
    fetchSettings();
    fetchNumbers();
    fetchStats();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get(`/whatsapp-settings?t=${Date.now()}`);
      if (response.data.configured) {
        setConfigured(true);
        setFormData({
          instance_id: response.data.settings.instance_id || '',
          access_token: response.data.settings.access_token || '',
          cta_url: response.data.settings.cta_url || '',
          cta_phone_number: response.data.settings.cta_phone_number || ''
        });
      }
    } catch (error) {
      console.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchNumbers = async () => {
    try {
      const res = await api.get('/whatsapp/numbers');
      setNumbers(res.data.numbers || []);
    } catch { }
  };

  const fetchStats = async () => {
    try {
      const res = await api.get('/whatsapp/stats');
      setWaStats(res.data);
    } catch { }
  };

  const handleTestConnection = async () => {
    if (!formData.instance_id || !formData.access_token) {
      toast.error('Pehle Instance ID aur Access Token save karo');
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const response = await api.post('/test-whatsapp');
      setTestResult(response.data);
      if (response.data.success) {
        toast.success('WhatsApp connection successful!');
      } else {
        toast.error(response.data.error || 'Connection failed');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Test failed';
      setTestResult({ success: false, error: errorMsg });
      toast.error(errorMsg);
    } finally {
      setTesting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.instance_id || !formData.access_token) {
      toast.error('Instance ID aur Access Token required hai');
      return;
    }
    setSaving(true);
    try {
      await api.put('/whatsapp-settings', formData);
      await clearAppCache();
      toast.success('WhatsApp settings save ho gayi!');
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Settings save nahi hui');
      setSaving(false);
    }
  };

  const handleAddNumber = async () => {
    if (!newNumber.instance_id || !newNumber.access_token) {
      toast.error('Instance ID aur Access Token dono required hai');
      return;
    }
    setAddingNumber(true);
    try {
      await api.post('/whatsapp/numbers', newNumber);
      toast.success('New WhatsApp number add ho gaya!');
      setShowAddNumber(false);
      setNewNumber({ label: '', instance_id: '', access_token: '' });
      fetchNumbers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Number add nahi hua');
    } finally {
      setAddingNumber(false);
    }
  };

  const toggleNumber = async (id, currentState) => {
    try {
      await api.put(`/whatsapp/numbers/${id}`, { is_active: !currentState });
      toast.success(!currentState ? 'Number active ho gaya' : 'Number inactive ho gaya');
      fetchNumbers();
    } catch {
      toast.error('Toggle failed');
    }
  };

  const deleteNumber = async (id, label) => {
    if (!window.confirm(`"${label}" delete karna hai? Ye wapas nahi aayega.`)) return;
    try {
      await api.delete(`/whatsapp/numbers/${id}`);
      toast.success('Number delete ho gaya');
      fetchNumbers();
    } catch {
      toast.error('Delete failed');
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6 max-w-3xl mx-auto" data-testid="whatsapp-settings-page">
        {/* Header */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-xl">
                <MessageCircle size={24} className="text-green-600" />
              </div>
              <div>
                <CardTitle className="text-xl">WhatsApp Integration</CardTitle>
                <CardDescription>Configure watrend.co.za API for payment reminders</CardDescription>
              </div>
              {configured ? (
                <Badge className="ml-auto bg-green-100 text-green-700 gap-1"><CheckCircle size={14} /> Configured</Badge>
              ) : (
                <Badge className="ml-auto bg-amber-100 text-amber-700 gap-1"><AlertCircle size={14} /> Not Configured</Badge>
              )}
            </div>
          </CardHeader>
        </Card>

        {/* Anti-Ban Stats */}
        {waStats && (
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Shield size={18} className="text-indigo-600" />
                <CardTitle className="text-base">Anti-Ban Protection</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="p-2.5 rounded-lg bg-slate-50 border">
                  <p className="text-slate-500 text-xs">Min Delay</p>
                  <p className="font-bold text-slate-800">{waStats.config?.min_delay}s</p>
                </div>
                <div className="p-2.5 rounded-lg bg-slate-50 border">
                  <p className="text-slate-500 text-xs">Max Delay</p>
                  <p className="font-bold text-slate-800">{waStats.config?.max_delay}s</p>
                </div>
                <div className="p-2.5 rounded-lg bg-slate-50 border">
                  <p className="text-slate-500 text-xs">Hourly Limit</p>
                  <p className="font-bold text-slate-800">{waStats.config?.hourly_limit}/hr</p>
                </div>
                <div className="p-2.5 rounded-lg bg-slate-50 border">
                  <p className="text-slate-500 text-xs">Daily Limit</p>
                  <p className="font-bold text-slate-800">{waStats.config?.daily_limit}/day</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Multiple Numbers Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Phone size={18} className="text-green-600" />
                <CardTitle className="text-base">WhatsApp Numbers ({numbers.length})</CardTitle>
              </div>
              <Button size="sm" onClick={() => setShowAddNumber(true)} className="gap-1.5 bg-green-600 hover:bg-green-700 h-8 text-xs" data-testid="add-wa-number-btn">
                <Plus size={14} /> Add Number
              </Button>
            </div>
            <CardDescription>Multiple numbers se messages rotate honge - ek number pe load nahi padega</CardDescription>
          </CardHeader>
          <CardContent>
            {numbers.length === 0 ? (
              <div className="text-center py-8 text-slate-400 text-sm">
                <Phone size={32} className="mx-auto mb-2 opacity-40" />
                <p>Koi extra number nahi hai. Primary number se messages jayenge.</p>
                <p className="text-xs mt-1">Multiple numbers add karo for rotation & anti-ban protection</p>
              </div>
            ) : (
              <div className="space-y-3">
                {numbers.map((num) => (
                  <div key={num.id} className={`flex items-center gap-3 p-3 rounded-xl border ${num.is_active ? 'bg-green-50/50 border-green-200' : 'bg-slate-50 border-slate-200 opacity-60'}`} data-testid={`wa-number-${num.id}`}>
                    <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${num.is_active ? 'bg-green-500' : 'bg-slate-300'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-slate-800 truncate">{num.label}</p>
                      <p className="text-xs text-slate-400 font-mono truncate">ID: {num.instance_id}</p>
                    </div>
                    <div className="flex items-center gap-2 text-xs shrink-0">
                      <div className="text-right hidden sm:block">
                        <span className="text-slate-500">Today: </span>
                        <span className="font-semibold text-slate-700">{num.today_sent || 0}</span>
                        <span className="text-slate-400 ml-1">| Hr: </span>
                        <span className="font-semibold text-slate-700">{num.hour_sent || 0}</span>
                      </div>
                      <Switch checked={num.is_active} onCheckedChange={() => toggleNumber(num.id, num.is_active)} />
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600 hover:bg-red-50" onClick={() => deleteNumber(num.id, num.label)}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Primary Settings Form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Primary API Configuration</CardTitle>
            <CardDescription>Ye primary/fallback number hai - agar rotation numbers nahi hain toh isse message jayega</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Server size={16} className="text-slate-500" /> Instance ID *</Label>
                <Input data-testid="instance-id-input" placeholder="Enter your watrend.co.za Instance ID" value={formData.instance_id} onChange={(e) => setFormData({ ...formData, instance_id: e.target.value })} className="font-mono" />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Key size={16} className="text-slate-500" /> Access Token *</Label>
                <Input data-testid="access-token-input" type="password" placeholder="Enter your API Access Token" value={formData.access_token} onChange={(e) => setFormData({ ...formData, access_token: e.target.value })} className="font-mono" />
              </div>
              <div className="border-t border-slate-200 pt-6">
                <p className="text-sm font-semibold text-slate-700 mb-4">Message Buttons (Optional)</p>
                <div className="space-y-2 mb-4">
                  <Label className="flex items-center gap-2"><Link2 size={16} className="text-slate-500" /> Payment URL</Label>
                  <Input data-testid="cta-url-input" placeholder="https://payment-link.com" value={formData.cta_url} onChange={(e) => setFormData({ ...formData, cta_url: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Phone size={16} className="text-slate-500" /> Call Number</Label>
                  <Input data-testid="cta-phone-input" placeholder="+91 98765 43210" value={formData.cta_phone_number} onChange={(e) => setFormData({ ...formData, cta_phone_number: e.target.value })} />
                </div>
              </div>
              <div className="flex gap-3">
                <Button type="submit" data-testid="save-settings-btn" disabled={saving} className="flex-1 bg-green-600 hover:bg-green-700 h-12 gap-2">
                  {saving ? <><Loader2 size={18} className="animate-spin" /> Saving...</> : <><Save size={18} /> Save Settings</>}
                </Button>
                <Button type="button" data-testid="test-whatsapp-btn" disabled={testing || !configured} onClick={handleTestConnection} variant="outline" className="h-12 gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                  {testing ? <><Loader2 size={18} className="animate-spin" /> Testing...</> : <><Send size={18} /> Test Connection</>}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Test Result */}
        {testResult && (
          <Card className={testResult.success ? "border-green-300 bg-green-50" : "border-red-300 bg-red-50"}>
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                {testResult.success ? <CheckCircle size={24} className="text-green-600 shrink-0" /> : <AlertCircle size={24} className="text-red-600 shrink-0" />}
                <div>
                  <p className={`font-semibold ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
                    {testResult.success ? 'Connection Successful!' : 'Connection Failed'}
                  </p>
                  <p className={`text-sm mt-1 ${testResult.success ? 'text-green-700' : 'text-red-700'}`}>
                    {testResult.success ? `Test message sent to ${testResult.test_phone}` : testResult.error || testResult.message}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Info Card */}
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <Info size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-2">Multiple Numbers Kaise Kaam Karta Hai:</p>
                <ul className="list-disc list-inside space-y-1 text-blue-700 text-xs">
                  <li>Har message sabse kam use hue number se jayega (auto-rotation)</li>
                  <li>Ek number ki hourly/daily limit lag jaye toh dusra number use hoga</li>
                  <li>Active/Inactive toggle se temporarily kisi number ko pause kar sakte ho</li>
                  <li>Primary number fallback hai - agar koi rotation number nahi toh yahi use hoga</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Add Number Dialog */}
      <Dialog open={showAddNumber} onOpenChange={setShowAddNumber}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Plus size={18} className="text-green-600" /> New WhatsApp Number</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-sm">Label / Name</Label>
              <Input data-testid="new-wa-label" placeholder="e.g. Office Number, Sanjeev Phone" value={newNumber.label} onChange={(e) => setNewNumber({ ...newNumber, label: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Instance ID *</Label>
              <Input data-testid="new-wa-instance" placeholder="watrend.co.za Instance ID" value={newNumber.instance_id} onChange={(e) => setNewNumber({ ...newNumber, instance_id: e.target.value })} className="font-mono" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Access Token *</Label>
              <Input data-testid="new-wa-token" type="password" placeholder="API Access Token" value={newNumber.access_token} onChange={(e) => setNewNumber({ ...newNumber, access_token: e.target.value })} className="font-mono" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddNumber(false)}>Cancel</Button>
            <Button onClick={handleAddNumber} disabled={addingNumber} className="bg-green-600 hover:bg-green-700 gap-1.5" data-testid="confirm-add-wa-number">
              {addingNumber ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
              Add Number
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
